import React from "react";
import moment from "moment";
import { withRouter } from "react-router-dom";

import LocationFormat from "../../../../utils/LocationFormat";
import Currency from "../../../../utils/Currency";
import BaseTable from "../BaseTable";
import General from "../../../../utils/General";
import FetchHelper from "../../../../utils/FetchHelper";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const TYPE_FILTERS = {
  name: { api: "type", display: "Type" },
  values: [
    { label: "All", value: null },
    { label: "Delivery", value: "delivery" },
    { label: "Return", value: "return" },
  ],
};

class DriversPaymentsTable extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      start_date: "",
      end_date: "",
      selected_month: "",
      selected_year: "",
      drivers: [],
      selected_driver: "",
      exporting: false,
    };

    this.table = React.createRef();
  }

  componentDidMount() {
    this._loadDrivers();
  }

  /** -------------------------------------------------------
   * Load Drivers For Dropdown
   --------------------------------------------------------- */
  async _loadDrivers() {
    try {
      const response = await FetchHelper.get(window.Api.Drivers + "?page_size=9999");
      this.setState({ drivers: response?.results || [] });
    } catch (err) {
      console.error("Failed to load drivers:", err);
    }
  }

  manualReload() {
   this.setState({ updated: !this.state.updated });
}


  async _export(type = "xlsx") {
  try {
    this.setState({ exporting: true });

    // ✅ Get ONLY filtered data from BaseTable UI
    const filteredData = this.table.current?.state?.data || [];

    if (!filteredData.length) {
      alert("No filtered data to export.");
      return;
    }

    // Convert to export format
    const exportData = filteredData.map((p) => ({
      Invoice: p.id,
      Driver: p?.driver?.user
        ? `${p.driver.user.first_name} ${p.driver.user.last_name}`
        : "-",
      Type: p.type,
      Amount: Currency.format(p.amount),
      Date: moment(p.created_at).format("DD MMM YYYY - hh:mm A"),
    }));

    // Calculate driver totals
    const driverTotals = {};
    filteredData.forEach((p) => {
      const driverName = p?.driver?.user
        ? `${p.driver.user.first_name} ${p.driver.user.last_name}`
        : "-";
      
      if (!driverTotals[driverName]) {
        driverTotals[driverName] = 0;
      }
      driverTotals[driverName] += p.amount || 0;
    });

    // Convert driver totals to array format with S/No
    const summaryData = Object.entries(driverTotals).map(([driver, total], index) => ({
      "S/No": index + 1,
      Driver: driver,
      "Total Amount": Currency.format(total)
    }));

    // Add grand total
    const grandTotal = Object.values(driverTotals).reduce((sum, val) => sum + val, 0);
    summaryData.push({
      "S/No": "",
      Driver: "GRAND TOTAL",
      "Total Amount": Currency.format(grandTotal)
    });

    // ---------------- CSV EXPORT ----------------
    if (type === "csv") {
      // Create main data worksheet
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      
      // Add empty row for spacing
      const startRow = exportData.length + 2;
      
      // Add summary table header
      XLSX.utils.sheet_add_json(worksheet, [{ "S/No": "", Driver: "", "Total Amount": "" }], {
        skipHeader: true,
        origin: startRow
      });
      
      // Add summary title
      XLSX.utils.sheet_add_json(worksheet, [{ "S/No": "", Driver: "DRIVER PAYMENT SUMMARY", "Total Amount": "" }], {
        skipHeader: true,
        origin: startRow + 1
      });
      
      // Add summary data
      XLSX.utils.sheet_add_json(worksheet, summaryData, {
        skipHeader: false,
        origin: startRow + 2
      });
      
      const csvData = XLSX.utils.sheet_to_csv(worksheet);
      const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
      saveAs(blob, `driver_payments_filtered_${Date.now()}.csv`);
      return;
    }

    // ---------------- XLSX EXPORT ----------------

    // Create main worksheet with payment details
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    
    // Add empty row for spacing
    const startRow = exportData.length + 2;
    
    // Add summary table header
    XLSX.utils.sheet_add_json(worksheet, [{ Driver: "", "Total Amount": "" }], {
      skipHeader: true,
      origin: startRow
    });
    
    // Add summary title
    XLSX.utils.sheet_add_json(worksheet, [{ Driver: "DRIVER PAYMENT SUMMARY", "Total Amount": "" }], {
      skipHeader: true,
      origin: startRow + 1
    });
    
    // Add summary data
    XLSX.utils.sheet_add_json(worksheet, summaryData, {
      skipHeader: false,
      origin: startRow + 2
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "DriverPayments");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, `driver_payments_filtered_${Date.now()}.xlsx`);

  } catch (err) {
    console.error("Export failed:", err);
    alert("Export failed.");
  } finally {
    this.setState({ exporting: false });
  }
}










  /** -------------------------------------------------------
   * TABLE COLUMNS
   --------------------------------------------------------- */
  _getColumns() {
    return [
      {
        Header: () => <strong className="header__clr">INVOICE NO.</strong>,
        id: "invoice_number",
        // Cell: (row) => <span>{row.original?.id || "-"}</span>,
         Cell: (row) => <span>{row.original?.order || "-"}</span>,
      },
      {
        Header: () => <strong className="header__clr">DRIVER</strong>,
        id: "driver",
        Cell: (row) => {
          let d = row.original?.driver?.user;
          return d ? `${d.first_name} ${d.last_name}` : "-";
        },
      },
      {
        Header: () => <strong className="header__clr">PAYMENT TYPE</strong>,
        id: "type",
        Cell: (row) => {
          let p = row.original;
          let className =
            p.type === "delivery"
              ? "label-light-success"
              : p.type === "return"
              ? "label-light-info"
              : "";

          return (
            <span className={`label ${className} label-inline label-pill`}>
              {p.type}
            </span>
          );
        },
      },
      {
        Header: () => <strong className="header__clr">AMOUNT</strong>,
        id: "amount",
        Cell: (row) => Currency.format(row.original.amount) || "-",
      },
      {
        Header: () => <strong className="header__clr">DATE / TIME</strong>,
        id: "created_at",
        Cell: (row) =>
          moment(row.original.created_at).format("DD MMM YYYY - hh:mm A"),
      },
    ];
  }

  /** -------------------------------------------------------
   * RENDER FILTER SECTION
   --------------------------------------------------------- */
  _renderFilters() {
    const { start_date, end_date, selected_month, selected_year, drivers, selected_driver } =
      this.state;

    const years = [2022, 2023, 2024, 2025];

    return (
  <div className="row mb-4 bg-white shadow-sm rounded" style={{ padding: "30px 20px", margin:"10px 0px" }}>

  {/* Title */}
  <div className="col-12 mb-3">
    <h4 className="mb-0 font-weight-bold">Drivers Payroll</h4>
  </div>

  {/* Start Date */}
  <div className="col-md-2 mb-3">
    <label className="font-weight-bold">Start Date</label>
    <input
      type="date"
      className="form-control"
      value={start_date}
      onChange={(e) => this.setState({ start_date: e.target.value })}
    />
  </div>

  {/* End Date */}
  <div className="col-md-2 mb-3">
    <label className="font-weight-bold">End Date</label>
    <input
      type="date"
      className="form-control"
      value={end_date}
      onChange={(e) => this.setState({ end_date: e.target.value })}
    />
  </div>

  {/* Month */}
  <div className="col-md-2 mb-3">
    <label className="font-weight-bold">Month</label>
    <select
      className="form-control"
      value={selected_month}
      onChange={(e) => this.setState({ selected_month: e.target.value })}
    >
      <option value="">All</option>
      {moment.months().map((m, i) => (
        <option key={i} value={i + 1}>{m}</option>
      ))}
    </select>
  </div>

  {/* Year */}
  <div className="col-md-2 mb-3">
    <label className="font-weight-bold">Year</label>
    <select
      className="form-control"
      value={selected_year}
      onChange={(e) => this.setState({ selected_year: e.target.value })}
    >
      <option value="">All</option>
      {years.map((y) => (
        <option key={y} value={y}>{y}</option>
      ))}
    </select>
  </div>

  {/* Driver Dropdown */}
  <div className="col-md-2 mb-3">
    <label className="font-weight-bold">Driver</label>
    <select
      className="form-control"
      value={selected_driver}
      onChange={(e) => this.setState({ selected_driver: e.target.value })}
    >
      <option value="">All Drivers</option>
      {drivers.map((d) => (
        <option key={d.id} value={d.id}>
          {d.user.first_name} {d.user.last_name}
        </option>
      ))}
    </select>
  </div>

  {/* Apply Filters Button */}
  <div className="col-md-2 d-flex align-items-end mb-3">
    {/* <button
      className="btn btn-primary btn-block"
      onClick={() => this.setState({ updated: !this.state.updated })}
    >
      Apply
    </button> */}


    <button
  className="btn btn-primary btn-block"
  onClick={() => this.table.current.manualReload()}
>
  Apply
</button>
  </div>
</div>



    );
  }

  /** -------------------------------------------------------
   * RENDER EXPORT BUTTONS
   --------------------------------------------------------- */
  _renderExportButtons() {
    return (
      <div className="d-flex justify-content-end mb-3">
        <button
          className="btn btn-success btn-sm mr-2"
          disabled={this.state.exporting}
          onClick={() => this._export("xlsx")}
        >
          {this.state.exporting ? (
            <span className="spinner-border spinner-border-sm mr-2"></span>
          ) : null}
          Export XLSX
        </button>

        <button
          className="btn btn-info btn-sm"
          disabled={this.state.exporting}
          onClick={() => this._export("csv")}
        >
          {this.state.exporting ? (
            <span className="spinner-border spinner-border-sm mr-2"></span>
          ) : null}
          Export CSV
        </button>
      </div>
    );
  }

  /** -------------------------------------------------------
   * MAIN RENDER
   --------------------------------------------------------- */
  render() {
    const { isLoading } = this.state;

    const columns = this._getColumns();

    return (
      <>
        {this._renderFilters()}
        {this._renderExportButtons()}

        <BaseTable
          ref={this.table}
          title={this.props.title}
          loading={isLoading}
          endpoint={`${window.Api.DriverPayments}`}
          noDataMessage={"No drivers payments found"}
          columns={columns}
          filters={[TYPE_FILTERS]}
          extraParams={{
            start_date: this.state.start_date,
            end_date: this.state.end_date,
            month: this.state.selected_month,
            year: this.state.selected_year,
            driver_id: this.state.selected_driver,
          }}
          showPagination={true}
          defaultSorted={[{ id: "created_at", desc: true }]}
        />
      </>
    );
  }
}

DriversPaymentsTable.defaultProps = {
  title: "Drivers Payments",
  showPagination: true,
  showPaginationTop: true,
  showPaginationBottom: true,
};

export default withRouter(DriversPaymentsTable);
















